import React, { useState, useMemo, useCallback } from 'react'
import { Button, Checkbox, Icon } from 'semantic-ui-react';
import Table from 'components/Table';
import { DateTime } from 'luxon'
import { numFormat } from '@rededompedro/utils';
import produce from 'immer';
import { toast } from 'react-toastify';
import { tap, finalize } from 'rxjs/operators';
import request from 'services/http';
import { useSubscription } from 'utils';

export default function GerarExibicao({ registros, setRegistros, form, operacoes, tipos, periodos, empresas }) {

    const [registrosSelecionados, setRegistrosSelecionados] = useState([])
    const [isSubmiting, setIsSubmiting] = useState(false)

    function handleSubmit() {
        if (registrosSelecionados.length)
            setIsSubmiting(true)
        else 
            toast.info('Selecione pelo menos um registro!')
    }

    function handleCheckboxChange(_, { value, checked }) {
        if (checked && !registrosSelecionados.includes(value)) {
            setRegistrosSelecionados(produce(registrosSelecionados, draft => {
                draft.push(value)
            }))
        } else if (!checked && registrosSelecionados.includes(value)) {
            setRegistrosSelecionados(produce(registrosSelecionados, draft => {
                let idx = draft.findIndex(x => x === value)
                draft.splice(idx, 1)
            }))
        }
    }

    function handleMarkAllChange(_, { checked }) {
        if (checked)
            setRegistrosSelecionados(registros.filter(x => !x.alreadyUsed).map(x => form.tipo === 0 ? x.seq : x.id) )
        else
            setRegistrosSelecionados([])
    }

    const clearState = useCallback(function () {
        setRegistros([])
        setRegistrosSelecionados([])
    }, [setRegistros, setRegistrosSelecionados])

    const btnSubmit = (
        <Button animated='fade' className='w-25' color='green' onClick={handleSubmit} loading={isSubmiting} disabled={isSubmiting}>
            <Button.Content visible>Gerar</Button.Content>
            <Button.Content hidden><Icon name='send' /></Button.Content>
        </Button>
    )

    const submitForm$ = useMemo(() => request({ 
        url: '/dre/pdd/gerar', 
        method: 'POST', 
        body: { ...form, registrosSelecionados } 
    }).pipe( 
        tap( () => {
            toast.success('PDD gerado com sucesso!')
            clearState()
        }),
        finalize(() => setIsSubmiting(false))
    ), [form, registrosSelecionados, clearState])

    useSubscription(submitForm$, isSubmiting)

    let config = form.tipo !== 0 ? {
        columns: [
            { field: 'codigo', text: 'Código', sort: true },
            { field: 'cliente', text: 'Cliente', sort: true },
            { field: 'documento', text: 'Fatura', sort: true },
            { field: 'emissao', text: 'Emissão', sort: true },
            { field: 'vencto', text: 'Vencimento', sort: true },
            { field: 'valor', text: 'Valor', formatter: value => numFormat(value), sort: true },
            { field: 'selection', text: <Checkbox label='Marcar todos' onChange={handleMarkAllChange} />, sort: false, cellProps: { textAlign: 'center' } },
        ],
        footer: [
            {
                columns: [
                    { colSpan: 5, content: 'Total Geral', textAlign: 'center', style: { bottom: 60, zIndex: 6 } },
                    { colSpan: 2, content: numFormat(registros.map(x => x.valor).reduce((a, b) => a + b, 0)), style: { bottom: 60 } }
                ]
            },
            {
                columns: [
                    { colSpan: 5, content: 'Total Marcado', textAlign: 'center', style: { bottom: 30, zIndex: 6 } },
                    { colSpan: 2, content: numFormat((registros.filter(x => registrosSelecionados.includes(x.id)).map(x => x.valor).reduce((a, b) => a + b, 0))), style: { bottom: 30, zIndex: 6 } }
                ]
            },
            {
                columns: [
                    { colSpan: 5, content: 'Total Disponível', textAlign: 'center', style: { bottom: 0, zIndex: 6 } },
                    { colSpan: 2, content: numFormat((registros.filter(x => !x.alreadyUsed).map(x => x.valor).reduce((a, b) => a + b, 0) - registros.filter(x => registrosSelecionados.includes(x.id)).map(x => x.valor).reduce((a, b) => a + b, 0))), style: { bottom: 0, zIndex: 6 } }
                ]
            },
            {
                columns: [
                    { className: 'actions', colSpan: 7, content: btnSubmit, textAlign: 'right' }
                ]
            }
        ],
        data: registros.length ? registros.map(x => {
            return {
                "codigo": x.codigo,
                "cliente": x.cliente,
                "documento": x.documento,
                "emissao": DateTime.fromFormat(x.emissao + "", "yyyyMMddHHmmss").toFormat('dd/MM/yyyy HH:mm:ss'),
                "vencto": DateTime.fromFormat(x.vencto + "", "yyyyMMdd").toFormat('dd/MM/yyyy'),
                "valor": x.valor,
                "selection": !x.alreadyUsed ? <Checkbox value={x.id} checked={registrosSelecionados.includes(x.id)} onChange={handleCheckboxChange} /> : '',
                "rowProps": { disabled: x.alreadyUsed }
            }
        }) : [],
        filter: true
    } : {
        columns: [
            { field: 'cliente', text: 'Cliente', sort: true },
            { field: 'data', text: 'Data', sort: true },
            { field: 'motivo', text: 'Motivo', sort: true },
            { field: 'cheque', text: 'Nº Cheque', sort: true },
            { field: 'valor', text: 'Valor', formatter: value => numFormat(value), sort: true },
            { field: 'selection', text: <Checkbox label='Marcar todos' onChange={handleMarkAllChange} />, sort: false, cellProps: { textAlign: 'center' } },
        ],
        footer: [
            {
                columns: [
                    { colSpan: 5, content: 'Total Geral', textAlign: 'center', style: { bottom: 60, zIndex: 6 } },
                    { colSpan: 2, content: numFormat(registros.map(x => x.valor).reduce((a, b) => a + b, 0)), style: { bottom: 60 } }
                ]
            },
            {
                columns: [
                    { colSpan: 5, content: 'Total Marcado', textAlign: 'center', style: { bottom: 30, zIndex: 6 } },
                    { colSpan: 2, content: numFormat((registros.filter(x => registrosSelecionados.includes(x.seq)).map(x => x.valor).reduce((a, b) => a + b, 0))), style: { bottom: 30, zIndex: 6 } }
                ]
            },
            {
                columns: [
                    { colSpan: 5, content: 'Total Disponível', textAlign: 'center', style: { bottom: 0, zIndex: 6 } },
                    { colSpan: 2, content: numFormat((registros.filter(x => !x.alreadyUsed).map(x => x.valor).reduce((a, b) => a + b, 0) - registros.filter(x => registrosSelecionados.includes(x.seq)).map(x => x.valor).reduce((a, b) => a + b, 0))), style: { bottom: 0, zIndex: 6 } }
                ]
            },
            {
                columns: [
                    { className: 'actions', colSpan: 7, content: btnSubmit, textAlign: 'right' }
                ]
            }
        ],
        data: registros.length ? registros.map(x => {
            return {
                "cliente": x.cliente,
                "data": x.data_emissao,
                "motivo": x.motivo,
                "cheque": x.numero_cheque,
                "valor": x.valor,
                "selection": !x.alreadyUsed ? <Checkbox value={x.seq} checked={registrosSelecionados.includes(x.seq)} onChange={handleCheckboxChange} /> : '',
                "rowProps": { disabled: x.alreadyUsed }
            }
        }) : [],
        filter: true
    }
    
    return (
        <>
            <div className="row">
                <div className="col-12 col-md d-flex align-items-end">
                    <p className='mb-0'>
                        <strong>Empresa:</strong> {empresas.find(x => x && x._id === form.empresa).nomeSecundario} - {periodos.find(x => x && x.id === form.periodo).descricao}
                        <span className='px-1'>/</span>
                        <strong>Periodo: </strong> {form.dataIni} à {form.dataFim}
                        <span className='px-1'>/</span>
                        <strong>Operação: </strong>{operacoes.find(x => x && x.id === form.operacao).descricao} - {tipos.find(x => x && x.tipo_entrada === form.tipo).descricao}
                    </p>
                </div>
                <div className='col-12 col-md-2'>
                    <Button animated='fade' size='tiny' color='orange' floated='right' onClick={clearState}>
                        <Button.Content visible><Icon name='arrow left' /></Button.Content>
                        <Button.Content hidden>Voltar</Button.Content>
                    </Button>
                </div>
            </div>
            <Table id='table-registros' compact sortable selectable {...config} />
        </>
    )
}
